import React from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowUpRight } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { MONETISEUP_LOGO, XYLE_LOGO, EXTERNAL } from "@/constants/brand";

const columns = [
  {
    title: "Product",
    links: [
      { id: "services", label: "Services", href: "/#services" },
      { id: "publishers", label: "For Publishers", href: "/#publishers" },
      { id: "advertisers", label: "For Advertisers", href: "/#advertisers" },
      { id: "manga", label: "Manga Monetisation", href: "/manga", internal: true },
    ],
  },
  {
    title: "Company",
    links: [
      { id: "about", label: "About", href: "/#about" },
      { id: "help", label: "Help Center", href: "/help-center", internal: true },
      { id: "abuse", label: "Report Abuse", href: "/abuse", internal: true },
      { id: "opt-out", label: "Opt Out", href: "/opt-out", internal: true },
      { id: "privacy", label: "Privacy Policy", href: "/privacy-policy", internal: true },
      { id: "terms", label: "Terms & Conditions", href: "/terms-conditions", internal: true },
    ],
  },
  {
    title: "Platforms",
    links: [
      { id: "dashboard", label: "Dashboard", href: EXTERNAL.dashboard, external: true },
      { id: "signup", label: "Sign Up", href: EXTERNAL.signup, internal: true },
      { id: "demo", label: "Live Demo", href: "/demo", internal: true },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="relative pt-16 pb-10 mt-12 border-t border-white/5"
    >
      <div className="aurora-orb bg-[#0047FF] w-[500px] h-[500px] -bottom-40 -left-40" />

      <div className="container-x relative z-10">
        <div className="grid lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 mb-10">
          <div>
            <img
              src={MONETISEUP_LOGO}
              alt="MonetiseUP"
              className="h-10 w-auto mb-5"
              style={{ filter: "drop-shadow(0 0 18px rgba(0,240,255,0.25))" }}
            />
            <p className="text-white/60 text-sm max-w-xs mb-6">
              Target. Engage. Excel. Powering your advertising success with
              advanced ad technology that drives exceptional results.
            </p>
            <a
              href={`mailto:${EXTERNAL.email}`}
              data-testid={FOOTER.link("email")}
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#00F0FF] transition-colors mb-5"
            >
              <Mail className="w-4 h-4" />
              {EXTERNAL.email}
            </a>

            {/* A Product of Xyle */}
            <div className="pt-4 border-t border-white/5">
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  A Product of
                </span>
                <a
                  href="https://xyle.tech"
                  target="_blank"
                  rel="noreferrer"
                  data-testid={FOOTER.link("xyle")}
                  className="inline-flex items-center group"
                  aria-label="Xyle"
                >
                  <img
                    src={XYLE_LOGO}
                    alt="Xyle"
                    className="h-7 md:h-8 w-auto opacity-90 group-hover:opacity-100 transition-opacity"
                  />
                </a>
              </div>
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.id}>
                    {l.external ? (
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={FOOTER.link(l.id)}
                        className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1 group"
                      >
                        {l.label}
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </a>
                    ) : l.internal ? (
                      <Link
                        to={l.href}
                        data-testid={FOOTER.link(l.id)}
                        className="text-sm text-white/70 hover:text-white"
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        data-testid={FOOTER.link(l.id)}
                        className="text-sm text-white/70 hover:text-white"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-8 border-t border-white/5">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} MonetiseUP. All rights reserved.
            Innovating digital advertising since 2024.
          </p>
          <div className="flex items-center gap-4 text-xs text-white/40">
            <Link to="/privacy-policy" className="hover:text-white">
              Privacy
            </Link>
            <span>·</span>
            <Link to="/terms-conditions" className="hover:text-white">
              Terms
            </Link>
            <span>·</span>
            <Link to="/help-center" className="hover:text-white">
              Help
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
